import { gql, useQuery } from '@apollo/client';
import { useAuth } from '../../context/AuthContext';

/**
 * The signed-in user's tinycms sites (a site is an account holding tinycms),
 * for the header's site switcher and the All sites page ("tinycms UX
 * optimization review v6.zip", Prototype v3).
 */

export const TINYCMS_SITES = gql`
  query TinycmsSites {
    tinycmsSites {
      accountId
      name
      role
      plan
      status
      host
      siteUrl
      iconUrl
      thumbnailUrl
      phoneUrl
      thumbnailAt
      visibility
      waiting
      lastPublishedAt
      ownerEmail
      createdAt
    }
  }
`;

export const CREATE_TINYCMS_SITE = gql`
  mutation CreateTinycmsSite($input: CreateTinycmsSiteInput!) {
    createTinycmsSite(input: $input) {
      id
      name
    }
  }
`;

export interface SiteSummary {
  accountId: string;
  name: string;
  role: string;
  plan: string | null;
  status: string;
  host: string | null;
  siteUrl: string | null;
  iconUrl: string | null;
  /** The live homepage's screenshot (a signed path on the API), or null until the first. */
  thumbnailUrl?: string | null;
  /** The same homepage as a phone shows it (same version), or null: then no phone is drawn. */
  phoneUrl?: string | null;
  thumbnailAt?: string | null;
  /** Who can see the live site. */
  visibility?: 'public' | 'password' | 'offline';
  waiting: number;
  lastPublishedAt: string | null;
  ownerEmail: string | null;
  createdAt: string;
}

export function useTinycmsSites(skip = false) {
  // Not before sign-in has settled: asked too early, it fails and the header
  // shows "Your site" with no sites in its menu (QA round 7, cold load).
  const { isAuthenticated, isLoading } = useAuth();
  const { data, loading, error, refetch } = useQuery<{ tinycmsSites: SiteSummary[] }>(
    TINYCMS_SITES,
    {
      skip: skip || isLoading || !isAuthenticated,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );
  return {
    sites: data?.tinycmsSites || [],
    // Still finding out: no list yet, and not given up.
    loading: !data && !error ? true : loading,
    error: data ? null : error || null,
    refetch,
  };
}

/** A steady hue per site, for its initial tile and card tint. */
export function siteHue(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
}

/**
 * The All sites card's role, shown only when it isn't Owner: someone added to
 * the site edits it ("Editor"; the design dropped "owned by …").
 */
export const cardRoleLabel = (role: string) =>
  role === 'admin' ? null : role === 'member' ? 'Editor' : roleLabel(role);

/** The stamp on a card's preview: who can't see the live site. */
export const visibilityStamp = (v?: string) =>
  v === 'password' ? 'Password protected' : v === 'offline' ? 'Offline' : null;

export const roleLabel = (role: string) =>
  role === 'admin' ? 'Owner' : role ? role[0].toUpperCase() + role.slice(1) : 'Member';

/** "Business plan"; null for an account with no tinycms plan on record. */
export const planLabel = (plan: string | null) =>
  plan ? `${plan[0].toUpperCase()}${plan.slice(1)} plan` : null;

/** "Published 2:41 PM" · "Published yesterday" · "Published Oct 8" · "Nothing published yet". */
export function publishedLine(at: string | null, now = new Date()) {
  if (!at) return 'Nothing published yet';
  const d = new Date(at);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  if (diff === 0)
    return `Published ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  if (diff === 1) return 'Published yesterday';
  return `Published ${d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  })}`;
}
